import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';
import { environment } from '@environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from '@app/interfaces/user.interface';
import { Observable } from 'rxjs';

interface AuthenticationOptions {
  redirectUser: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  public otpToken: string | null = null;
  public changePasswordSuccess = false;

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  authentication(
    login: string,
    password: string,
    deviceToken: string = null,
    options: AuthenticationOptions = { redirectUser: true },
  ) {
    return this.http
      .post<any>(`${environment.authUrl}/authenticate`, { login, password, deviceToken })
      .pipe(
        tap((response) => {
          if (response.mfaRequired) {
            this.otpToken = response.otpToken;
            this.router.navigate(['/otp']);
          } else {
            const { user, permissions } = response;
            this.putCookieUser(user, permissions);
            if (options.redirectUser) {
              this.redirectUser(user);
            }
          }
        }),
      );
  }

  verifyOtp(otpCode, isTrusted, options: AuthenticationOptions = { redirectUser: true }) {
    return this.http
      .post<any>(`${environment.authUrl}/verify-otp`, {
        otpCode,
        isTrusted,
        deviceToken: this.getDeviceToken(),
      })
      .pipe(
        tap(({ user, permissions, deviceToken }) => {
          if (deviceToken) {
            this.updateDeviceTokenCookie(deviceToken);
          }
          this.putCookieUser(user, permissions);
          if (options.redirectUser) {
            this.redirectUser(user);
          }
        }),
      );
  }

  resendOtp() {
    return this.http.post<any>(`${environment.authUrl}/resend-otp`, {});
  }

  resetPasswordRequest(email: string) {
    return this.http
      .post<any>(`${environment.authUrl}/reset-password/request`, {
        email,
      })
      .pipe(
        tap(() => {
          this.changePasswordSuccess = true;
        }),
      );
  }

  getCurrentUser(): User {
    return this.cookieService.getObject('currentUser') as User;
  }

  connectPsc(code: string) {
    return this.http.post<any>(`${environment.authUrl}/psc/authenticate`, { code });
  }

  linkUserPsc(login: string, password: string) {
    const { token } = this.getCurrentUser();

    return this.http
      .post<any>(`${environment.authUrl}/psc/authenticateNewUser`, { login, password, token })
      .pipe(
        tap(({ user, permissions }) => {
          this.putCookieUser({ ...user, token }, permissions);
          this.redirectUser(user);
        }),
      );
  }

  listUserPsc(token: string) {
    return this.http.post<any>(`${environment.authUrl}/psc/users`, { token });
  }

  putCookieUser(user: Partial<User>, permissions?) {
    const expires = new Date();
    expires.setTime(expires.getTime() + 8 * 60 * 60 * 1000);
    this.cookieService.putObject('currentUser', this.getBasicUserForCookies(user), {
      domain: environment.cookieDomain,
      sameSite: 'lax',
      expires,
    });
    this.cookieService.putObject('companiesProfiles', user.companies, {
      domain: environment.cookieDomain,
      sameSite: 'lax',
      expires,
    });
    if (permissions) {
      this.cookieService.putObject('currentPermission', permissions, {
        domain: environment.cookieDomain,
        sameSite: 'lax',
        expires,
      });
    }
  }

  updateDeviceTokenCookie(deviceToken) {
    const expires = new Date();
    expires.setTime(expires.getTime() + 31 * 24 * 60 * 60 * 1000);
    this.cookieService.put('deviceToken', deviceToken, {
      domain: environment.cookieDomain,
      sameSite: 'lax',
      expires,
    });
  }

  getDeviceToken() {
    return this.cookieService.get('deviceToken');
  }

  clearCookieUser() {
    this.cookieService.remove('currentUser', {
      domain: environment.cookieDomain,
      sameSite: 'lax',
    });
    this.cookieService.remove('currentPermission', {
      domain: environment.cookieDomain,
      sameSite: 'lax',
    });
  }

  getBasicUserForCookies(user) {
    const {
      _id,
      token,
      firstName,
      lastName,
      fullName,
      type,
      adminType,
      isPSC,
      isAnonymizer,
      language,
      biologicDataUnit,
      prescriptionTemplateAuthorization,
      prescriptionTemplate,
      enableScale,
      enableTensio,
      jfse,
      profilId,
      cguAccepted,
    } = user;

    return {
      _id,
      token,
      firstName,
      lastName,
      fullName,
      type,
      adminType,
      isPSC,
      isAnonymizer,
      language,
      biologicDataUnit,
      prescriptionTemplateAuthorization,
      prescriptionTemplate,
      enableScale: enableScale === undefined ? true : enableScale,
      enableTensio: enableTensio === undefined ? true : enableTensio,
      jfse,
      profilId,
      cguAccepted,
    };
  }

  redirectUser(user: User) {
    if (user.passwordExpire) {
      this.router.navigate(['/renewPassword']);
    } else if (user.type !== 'admin' && !user.cguAccepted) {
      this.router.navigate(['/cgu']);
    } else {
      const regex = new RegExp(environment.newcardURL);
      const returnUrl = this.route.snapshot.queryParams?.returnUrl;

      if (regex.test(returnUrl)) {
        document.location.href = returnUrl;
      } else {
        document.location.href = environment.psp;
      }
    }
  }

  logoutFromPSC(): Observable<void> {
    const { token } = this.getCurrentUser();

    return this.http.post<void>(`${environment.authUrl}/psc/logout`, { token });
  }

  changePassword(password, confirmPassword) {
    const { token } = this.getCurrentUser();

    return this.http.post<void>(`${environment.authUrl}/renewPassword`, {
      token,
      password,
      confirmPassword,
    });
  }

  resetPassword(password, confirmPassword, resetPasswordToken) {
    return this.http.post<void>(`${environment.authUrl}/reset-password/change`, {
      password,
      confirmPassword,
      resetPasswordToken,
    });
  }

  verifyResetPasswordToken(token) {
    return this.http.post<any>(`${environment.authUrl}/verify-resetPasswordToken`, {
      resetPasswordToken: token,
    });
  }
}
